import { convertLanguage } from '@frontend/common';
import { HtmlTemplateEditorProvider, useHtmlTemplateEditor } from '@frontend/html-template-editor';
import { CommonMessage } from '@frontend/lang';
import { NotificationContent } from '@frontend/notification/types';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationContentEditor } from './editor.component';
import { NotificationChannelLabel } from './notification-channel-label';

export interface NotificationContentEditorLayoutProps {
    content: NotificationContent;
    placeholders: string[];
    templateName: React.ReactNode;
    onPlaceholderDrag: React.DragEventHandler;
    readOnly?: boolean;
    className?: string;
    onCancel(): void;
}

export const NotificationContentEditorLayout = (props: NotificationContentEditorLayoutProps) => (
    <HtmlTemplateEditorProvider>
        <NotificationContentEditorLayoutBody {...props} />
    </HtmlTemplateEditorProvider>
);

const NotificationContentEditorLayoutBody = (props: NotificationContentEditorLayoutProps) => {
    const { insertPlaceholder } = useHtmlTemplateEditor();
    const [insertedPlaceholder, setInsertedPlaceholder] = useState<string | null>(null);
    const [editorHeight, setEditorHeight] = useState<number | null>(null);
    const insertedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const editorRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        return () => {
            if (insertedTimeout.current) clearTimeout(insertedTimeout.current);
        };
    }, []);

    const insertIntoEditor = (placeholder: string) => {
        insertPlaceholder(placeholder);
        setInsertedPlaceholder(placeholder);
        if (insertedTimeout.current) clearTimeout(insertedTimeout.current);
        insertedTimeout.current = setTimeout(() => setInsertedPlaceholder(null), 1200);
    };

    const formatPlaceholderLabel = (placeholder: string) => placeholder.replace(/_/g, ' ').toUpperCase();

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;

        const updateHeight = () => {
            const nextHeight = Math.round(editor.getBoundingClientRect().height);
            setEditorHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
        };

        updateHeight();
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(editor);

        return () => resizeObserver.disconnect();
    }, [props.content.id, props.readOnly]);

    return (
        <div className={`flex w-full min-w-0 items-start justify-start gap-4 ${props.className ?? ''}`}>
            <div
                className='flex min-h-0 w-96 shrink-0 flex-col items-start justify-start gap-4 overflow-hidden'
                style={editorHeight ? { height: editorHeight, maxHeight: editorHeight } : undefined}>
                <div className='bg-background-surface outline-border-default flex shrink-0 flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                    <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
                    <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                        <div className='flex flex-col items-start justify-center gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.NOTIFICATION_CONTENT.DEFAULT}</div>
                            <div className='text-foreground-default justify-start text-base font-medium'>{props.templateName}</div>
                        </div>
                        <div className='flex flex-col items-start justify-center gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-base font-medium'>
                                <FormattedMessage
                                    id='content-detail.channel'
                                    description='Label for a notification content channel'
                                    defaultMessage='Channel'
                                />
                            </div>
                            <div className='text-foreground-default justify-start text-base font-medium'>
                                <NotificationChannelLabel channel={props.content.channel} />
                            </div>
                        </div>
                        <div className='flex flex-col items-start justify-center gap-0.5 self-stretch'>
                            <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.FORMS.LANGUAGE}</div>
                            <div className='text-foreground-default justify-start text-base font-medium'>{convertLanguage(props.content.language)}</div>
                        </div>
                    </div>
                </div>
                {!props.readOnly && (
                    <div className='bg-background-surface outline-border-default flex min-h-0 flex-1 flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                        <div className='text-foreground-default justify-start text-xl font-semibold'>
                            <FormattedMessage
                                id='content-detail.placeholders'
                                description='Heading for the notification template placeholder list'
                                defaultMessage='Placeholders'
                            />
                        </div>
                        <div className='no-scrollbar flex min-h-0 flex-1 flex-col items-start justify-start gap-2 self-stretch overflow-auto'>
                            {props.placeholders.map((placeholder) => (
                                <div
                                    key={placeholder}
                                    role='button'
                                    tabIndex={0}
                                    onClick={() => insertIntoEditor(placeholder)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            insertIntoEditor(placeholder);
                                        }
                                    }}
                                    className='bg-border-default hover:outline-brand-primary inline-flex cursor-pointer items-center justify-between gap-2.5 self-stretch rounded-md px-3 py-2 outline-1 outline-offset-[-1px]'>
                                    <span
                                        draggable
                                        data-placeholder={placeholder}
                                        onDragStart={props.onPlaceholderDrag}
                                        className='flex min-w-0'>
                                        <span className='text-foreground-default truncate text-xs font-semibold'>{formatPlaceholderLabel(placeholder)}</span>
                                    </span>
                                    {insertedPlaceholder === placeholder && (
                                        <span className='text-status-success shrink-0 text-xs font-medium'>
                                            <FormattedMessage
                                                id='content-detail.placeholder-inserted'
                                                description='Temporary status shown after inserting a notification template placeholder'
                                                defaultMessage='Inserted'
                                            />
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div
                ref={editorRef}
                className='flex min-w-0 flex-1'>
                <NotificationContentEditor
                    content={props.content}
                    placeholders={props.placeholders}
                    readOnly={props.readOnly}
                    onCancel={props.onCancel}
                />
            </div>
        </div>
    );
};
